import { useMemo, useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../../routes';
import MantineTable from '../../../common/table/mantine';
import { rfsStatus } from '../../../constants';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconX } from '@tabler/icons-react';
import { ActionModal } from './actionModal';
import { crRoles } from '../../../constants';
import { AuthContext } from '../../../contexts/AuthContext';
import { axios } from '../../../utils/axios';
import { showAlert } from '../../../utils/alerts';

export const RfsTable = ({
  data,
  csvFilename,
  row,
  nestedTab,
  tabType,
  fetchRfs,
  loading: isLoading,
  leftButtons,
  isRfsCreated = 'all',
}) => {
  const [opened, setOpened] = useState(false);
  const [actionType, setActionType] = useState('');
  const [skus, setSkus] = useState([]);
  const [comment, setComment] = useState('');
  const [crNumber, setCrNumber] = useState('');
  const [attachment, setAttachment] = useState('');
  const [attachmentId, setAttachmentId] = useState('');
  const [crStatus, setCrStatus] = useState(null);
  const [error, setError] = useState(false);
  const { crUserRoles } = useContext(AuthContext);
  const isAdmin = crUserRoles.includes(crRoles.userAdmin);
  const [loading, setLoading] = useState(false);

  const handleOpenModal = (type, crNumber) => {
    if (!isAdmin) return;
    setActionType(type);
    setCrNumber(crNumber);
    setOpened(true);
  };

  const handleCloseModal = () => {
    setOpened(false);
    setComment('');
    setAttachment(null);
    setError(false);
  };
  const formattedAction = actionType === 'Close' ? 'closed' : 'cancelled';
  const referenceType = actionType === 'Close' ? 'forcely_closed' : 'forcely_cancelled';

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError(true);
      return;
    }

    console.log({
      action: actionType,
      comment,
      attachment,
    });

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('comment', comment);
      formData.append('action', formattedAction);
      formData.append('reference_type', referenceType);
      if (attachment) {
        formData.append('attachments', attachment);
      }
      if (attachmentId.length) {
        attachmentId.forEach((id) => formData.append('attachmentId[]', id));
      }
      const response = await axios.post(
        `/costing-request/manual-action-ticket/${crNumber}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.status === 200) {
        showAlert(
          {
            message: 'Submission successful!',
          },
          'success',
        );

        handleCloseModal();
        await fetch();
        await fetchRfs();
      } else {
        throw new Error(response.data.message || 'Submission failed.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showAlert(
        {
          message: 'Failed to submit. Please try again.',
        },
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/costing-request/manual-action-onticket/${crNumber}?action=${formattedAction}`,
      );
      if (response.status === 200) {
        setSkus(response.data);
        if (response.data.status) {
          setCrStatus(response.data.status);
        }
      } else {
        throw new Error('Failed to fetch updated data.');
      }
    } catch (error) {
      console.error('Error fetching updated data:', error);
    } finally {
      setLoading(false);
    }
  };

  const dropDownList = ['Pegasus', 'Production Site', 'OSTM'];
  let rfsCreatedColumns = useMemo(
    () => [
      {
        header: 'Actions',
        id: 'actions',
        size: 80,
        minSize: 80,
        maxSize: 120,
        Cell: ({ row }) => {
          const status = row.original?.status;
          const isEnabled = status === 'Costing in progress' || status === 'TP In Progress';
          return (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Tooltip label='Close the Request' withinPortal>
                <ActionIcon
                  color='green'
                  onClick={() =>
                    handleOpenModal('Close', row.original.costing_request_id, row.original.step_id)
                  }
                  style={{ textDecoration: 'none' }}
                  disabled={!isAdmin || !isEnabled}
                >
                  <IconCircleCheck />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Cancel the Request' withinPortal>
                <ActionIcon
                  color='red'
                  onClick={() =>
                    handleOpenModal('Cancel', row.original.costing_request_id, row.original.step_id)
                  }
                  style={{ textDecoration: 'none' }}
                  disabled={!isAdmin || !isEnabled}
                >
                  <IconX />
                </ActionIcon>
              </Tooltip>
            </div>
          );
        },
      },
      {
        header: 'RFS REQUEST',
        accessorKey: 'request_from',
        size: 200,
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: dropDownList,
          placeholder: 'Filter by Request',
          clearable: true,
        },
        mantineTableBodyCellProps: {
          style: { textAlign: 'center' }, // Center horizontally
        },
        Cell: ({ row }) => row.original?.request_from || 'N/A',
      },
      {
        header: 'Req ID',
        accessorKey: 'costing_request_id',
        size: 100,
        Cell: ({ row }) => {
          const { costing_request_id, status } = row.original;
          return (
            <Link
              to={
                ![rfsStatus.draft].includes(status.toLowerCase())
                  ? routes.costingRequests(costing_request_id).overview
                  : routes.costingRequests(costing_request_id).request
              }
            >
              {costing_request_id}
            </Link>
          );
        },
      },
      {
        header: 'RFS Number',
        id: 'rfs_number',
        accessorFn: (row) => row.rfs_number || 'N/A',
        size: 100,
        Cell: ({ row }) => {
          const info = row.original;
          const checkPegusus =
            info?.rfs_number &&
            (info?.rfs_number.includes('OS-') || info?.rfs_number.includes('PS-'));
          return info?.rfs_number ? (
            checkPegusus ? (
              <Link to={routes.project(info.rfs_number).root}>{info.rfs_number}</Link>
            ) : (
              <a
                href={`https://pegasus-abinbev.datastation.com/default/rfs/viewrfsoverview/${info?.rfs_number}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {info?.rfs_number}
              </a>
            )
          ) : (
            'N/A'
          );
        },
      },
      {
        header: 'SKU No',
        accessorKey: 'sku',
        size: 100,
        Cell: ({ row }) => row.original?.sku || 'N/A',
      },
      {
        header: 'SKU Description',
        accessorKey: 'sku_description',
        size: 250,
        Cell: ({ row }) => row.original?.sku_description || 'N/A',
      },
      {
        header: 'Requestor',
        accessorKey: 'userName',
      },
      {
        header: 'Submission Date',
        accessorKey: 'date',
        Cell: ({ row }) => row.original?.date || 'N/A',
      },
      {
        header: 'Type of request',
        accessorKey: 'type_of_request',
        Cell: ({ row }) => row.original?.type_of_request || 'N/A',
      },
      {
        header: 'Urgency',
        accessorKey: 'urgency',
        Cell: ({ row }) => row.original?.urgency || 'N/A',
      },
      {
        header: 'FSD Date',
        accessorKey: 'fsd_date',
        Cell: ({ row }) => row.original?.fsd_date || 'N/A',
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ row }) => row.original?.status || 'N/A',
      },
      {
        header: 'Steps In Progress',
        accessorKey: 'steps_in_progress',
        Cell: ({ row }) => row.original?.steps_in_progress || 'N/A',
      },
      {
        header: 'Production Plant',
        accessorKey: 'producing_plant',
        Cell: ({ row }) => row.original?.producing_plant || 'N/A',
      },
      {
        header: 'Selling Country',
        accessorKey: 'selling_country',
        Cell: ({ row }) => {
          const info = row.original;
          return info?.selling_country ? info.selling_country : 'N/A';
        },
      },
      {
        header: 'Buying Country',
        accessorKey: 'buying_country',
        Cell: ({ row }) => row.original?.buying_country || 'N/A',
      },
      {
        header: 'FP&A GCC SME',
        accessorKey: 'spoc_pricing_team',
        Cell: ({ row }) => row.original?.spoc_pricing_team || 'N/A',
      },
    ],
    [data, opened, actionType, comment, attachment, error],
  );

  let rfsCreationPendingColumns = useMemo(
    () => [
      {
        header: 'RFS Number',
        id: 'rfs_number',
        accessorFn: (row) => row.rfs_number || 'N/A',
        size: 100,
        Cell: ({ row }) => {
          const info = row.original;
          const checkPegusus =
            info?.rfs_number &&
            (info?.rfs_number.includes('OS-') || info?.rfs_number.includes('PS-'));
          return info?.rfs_number ? (
            checkPegusus ? (
              <Link to={routes.project(info.rfs_number).root}>{info.rfs_number}</Link>
            ) : (
              <a
                href={`https://pegasus-abinbev.datastation.com/default/rfs/viewrfsoverview/${info?.rfs_number}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {info?.rfs_number}
              </a>
            )
          ) : (
            'N/A'
          );
        },
      },
      {
        header: 'SKU No',
        accessorKey: 'sku',
        size: 100,
        Cell: ({ row }) => row.original?.sku || 'N/A',
      },
      {
        header: 'SKU Description',
        accessorKey: 'sku_description',
        size: 250,
        Cell: ({ row }) => row.original?.sku_description || 'N/A',
      },

      {
        header: 'Type of request',
        accessorKey: 'type_of_request',
        Cell: ({ row }) => row.original?.type_of_request || 'N/A',
      },

      {
        header: 'Producing Plant',
        accessorKey: 'producing_plant',
        Cell: ({ row }) => row.original?.producing_plant || 'N/A',
      },
      {
        header: 'Initiator',
        accessorKey: 'initiator',
        Cell: ({ row }) => row.original?.initiator || 'N/A',
      },
      {
        header: 'Gatekeeper',
        accessorKey: 'gate_keeper',
        Cell: ({ row }) => row.original?.gate_keeper || 'N/A',
      },
      {
        header: 'FSD Date',
        accessorKey: 'fsd_date',
        Cell: ({ row }) => row.original?.fsd_date || 'N/A',
      },
    ],
    [data, opened, actionType, comment, attachment, error],
  );

  const selectedColumns =
    isRfsCreated === 'created'
      ? rfsCreatedColumns
      : isRfsCreated === 'creation pending'
      ? rfsCreationPendingColumns
      : [];

  return (
    <>
      <MantineTable
        columns={selectedColumns}
        initialData={data}
        unique={'costing_request_id'}
        enableRowSelection={false}
        loading={isLoading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        enableExport={true}
        csvFilename={csvFilename}
        pageSize={10}
        enablePinning={true}
        initialState={{
          columnPinning: {
            left: [
              'actions',
              'request_from',
              'costing_request_id',
              'rfs_number',
              'sku',
              'sku_description',
            ],
            right: [' '],
          },
          showColumnFilters: true,
        }}
        leftButtons={leftButtons}
      />
      <ActionModal
        opened={opened}
        onClose={handleCloseModal}
        actionType={actionType}
        comment={comment}
        crNumber={crNumber}
        setComment={setComment}
        attachment={attachment}
        setAttachment={setAttachment}
        onSubmit={handleSubmit}
        error={error}
        row={row}
        nestedTab={nestedTab}
      />
    </>
  );
};
